/**
 * Sistema de campos de formulario.
 *
 * El maquetado original no tiene NI UN formulario, así que estos componentes no se «extraen»:
 * se derivan de los tokens para que un campo parezca de la misma familia que una tarjeta.
 *
 * ACCESIBILIDAD, QUE AQUÍ NO ES OPCIONAL:
 *  · `label` asociada con `htmlFor` (no envolviendo, que rompe el clic en algunos lectores).
 *  · El error se enlaza con `aria-describedby` y el campo se marca `aria-invalid`.
 *  · El error lleva SIEMPRE icono además de color.
 *  · La ayuda también se enlaza, para que se lea antes de escribir y no después de fallar.
 */
import {
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon } from '../Icon';
import {
  caretForSignificant,
  formatMoneyDisplay,
  normalizePastedMoney,
  parseMoneyDisplay,
  significantCount,
} from '../../lib/moneyInput';
import styles from './Field.module.css';

// ---------------------------------------------------------------------------
// Envoltorio
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  error?: string | null;
  help?: string;
  required?: boolean;
  /** Marca explícita de «opcional». Útil cuando casi todo el formulario es obligatorio. */
  optional?: boolean;
}

export function Field({ label, htmlFor, children, error, help, required, optional }: FieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;

  /**
   * El `id` y las relaciones ARIA se INYECTAN en el campo hijo.
   *
   * Antes se generaba el `id`, se ponía en el `htmlFor` de la etiqueta y ahí se quedaba: la
   * etiqueta apuntaba a un elemento que no existía. Visualmente idéntico, y roto para todo lo
   * demás — un lector de pantalla no anunciaba el nombre del campo, pulsar la etiqueta no
   * enfocaba el campo, y `getByLabel` no lo encontraba.
   *
   * Lo destapó un test e2e que no conseguía rellenar el formulario de alta de inmueble.
   *
   * Se inyecta también `aria-describedby` hacia la ayuda o el error, y `aria-invalid` cuando
   * hay error: es lo que hace que el mensaje se anuncie junto al campo en vez de quedar como
   * un texto suelto que nadie relaciona con nada.
   *
   * LÍMITE: la inyección va al hijo DIRECTO. Las primitivas de este fichero propagan las props
   * al elemento interno, así que funcionan aunque lleven envoltorio (el sufijo «€», por
   * ejemplo). Pero si envuelves el campo tú —un contenedor para posicionar un desplegable de
   * autocompletado, pongamos—, el `id` acabaría en tu `div`. En ese caso pasa `htmlFor` a
   * `Field` y el mismo `id` al campo, a mano.
   */
  const described = [help && !error ? `${id}-help` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');

  /**
   * Si el llamante pasa `htmlFor`, está asumiendo él la asociación y aquí NO se inyecta nada.
   *
   * Sin esta condición se duplicaba el identificador: `Field` lo ponía en el hijo directo —que
   * en un campo envuelto es el contenedor— y el llamante lo ponía además en el campo real. Dos
   * elementos con el mismo `id`, la etiqueta resolviendo al `div` y un `fill()` imposible.
   */
  const field =
    htmlFor === undefined && isValidElement(children)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id: (children.props as { id?: string }).id ?? id,
          ...(described ? { 'aria-describedby': described } : {}),
          ...(error ? { 'aria-invalid': true } : {}),
        })
      : children;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
        {optional && <span className={styles.optional}>(opcional)</span>}
      </label>

      {field}

      {help && !error && (
        <span className={styles.help} id={`${id}-help`}>
          {help}
        </span>
      )}

      {error && (
        <span className={styles.error} id={`${id}-error`} role="alert">
          <span className={styles.errorIcon}>
            <Icon name="info" size={13} />
          </span>
          {error}
        </span>
      )}
    </div>
  );
}

/** Atributos ARIA que deben acompañar a un control dentro de `Field`. */
export function describedBy(id: string, hasError: boolean, hasHelp: boolean): string | undefined {
  const parts = [hasError ? `${id}-error` : null, hasHelp && !hasError ? `${id}-help` : null].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(' ') : undefined;
}

// ---------------------------------------------------------------------------
// Controles
// ---------------------------------------------------------------------------

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  invalid?: boolean;
  /** Adorno a la derecha: `€`, `m²`, `%`. */
  suffix?: string;
  numeric?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, suffix, numeric, ...rest },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={[styles.control, invalid ? styles.invalid : '', numeric ? styles.numeric : '']
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    />
  );

  if (!suffix) return control;

  return (
    <div className={styles.affixed}>
      {control}
      <span className={styles.suffix} aria-hidden="true">
        {suffix}
      </span>
    </div>
  );
});

/** «15/03/1990», «15-3-1990» o «15031990» → `1990-03-15`; `null` si no es una fecha que existe. */
function isoDeTexto(texto: string): string | null {
  const partes = /^\s*(\d{1,2})[\s./-]?(\d{1,2})[\s./-]?(\d{4})\s*$/.exec(texto);
  if (!partes) return null;
  const [dia, mes, anio] = [Number(partes[1]), Number(partes[2]), Number(partes[3])];
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  const existe = fecha.getUTCDate() === dia && fecha.getUTCMonth() === mes - 1 && anio >= 1900;
  return existe ? fecha.toISOString().slice(0, 10) : null;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const textoDeIso = (iso: string): string => (ISO.test(iso) ? iso.split('-').reverse().join('/') : iso);

interface DateTextInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'placeholder' | 'inputMode'> {
  /** `YYYY-MM-DD`, o vacío. */
  value: string;
  /** La fecha en ISO; vacío si se borra; lo escrito tal cual si no es una fecha (y falla al guardar). */
  onChange: (value: string) => void;
}

/**
 * Una fecha que se ESCRIBE —«dd/mm/aaaa»— en vez del calendario del navegador.
 *
 * Para una fecha de nacimiento el calendario es la peor herramienta: hay que retroceder décadas,
 * y vacío, algunos navegadores pintan en gris la fecha de HOY, que parece un dato puesto por
 * defecto. Aquí vacío es vacío. Por fuera se comporta como un `type="date"`: recibe y devuelve
 * ISO. Lo que se escribe a medias se queda en el campo y no viaja hasta que es una fecha que
 * existe; si se deja así, viaja tal cual y el servidor lo rechaza en este mismo campo.
 */
export function DateTextInput({ value, onChange, invalid, onBlur, ...rest }: DateTextInputProps) {
  const [texto, setTexto] = useState(() => textoDeIso(value));
  const [tocado, setTocado] = useState(false);

  // Si el valor cambia desde fuera —lo rellena la lectura del DNI, se reinicia el panel—, se ve.
  useEffect(() => {
    const actual = texto.trim() === '' ? '' : (isoDeTexto(texto) ?? texto.trim());
    if (value !== actual) setTexto(textoDeIso(value));
    // Solo cuando cambia el valor de fuera: `texto` es lo que se está escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const incompleta = tocado && texto.trim() !== '' && isoDeTexto(texto) === null;

  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="dd/mm/aaaa"
      value={texto}
      invalid={invalid || incompleta}
      onChange={(event) => {
        const escrito = event.target.value;
        setTexto(escrito);
        onChange(escrito.trim() === '' ? '' : (isoDeTexto(escrito) ?? escrito.trim()));
      }}
      onBlur={(event) => {
        setTocado(true);
        onBlur?.(event);
      }}
    />
  );
}

interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={[styles.control, styles.textarea, invalid ? styles.invalid : '']
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    />
  );
});

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  invalid?: boolean;
  options: { value: string; label: string }[];
  /** Opción vacía inicial. Sin ella, un select obligatorio parece ya contestado. */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, options, placeholder, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={[styles.control, styles.select, invalid ? styles.invalid : '']
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

/**
 * Entrada de dinero.
 *
 * LA PIEZA DELICADA DEL SISTEMA. El usuario español escribe `1.250,50`; la API espera
 * `"1250.50"`; y el estado NUNCA puede ser un `number` de JavaScript, porque `1250.5` ya ha
 * perdido la distinción entre «1250,50 €» y «1250,5 €» y cualquier suma posterior arrastra el
 * error de coma flotante que el esquema evita a propósito con `numeric(14,2)`.
 *
 * Por eso el valor que sale de aquí es SIEMPRE un string decimal con punto.
 *
 * LO QUE SE VE, en cambio, lleva el punto de millar puesto solo mientras se escribe: `130000`
 * aparece como `130.000`. Un precio de seis cifras sin separador obliga a contar ceros, y un
 * cero de más o de menos en el precio de compra descuadra la rentabilidad de toda la cartera.
 * El formato vive en `lib/moneyInput.ts`; aquí solo está lo que depende del navegador: qué
 * tecla se ha pulsado y dónde dejar el cursor.
 */
export const MoneyInput = forwardRef<HTMLInputElement, InputProps & { onValueChange?: (value: string) => void }>(
  function MoneyInput({ onValueChange, onChange, value, ...rest }, ref) {
    const innerRef = useRef<HTMLInputElement | null>(null);
    /** Dónde poner el cursor tras el próximo pintado. Solo se rellena al teclear. */
    const caretRef = useRef<number | null>(null);
    const display = formatMoneyDisplay(value == null ? '' : String(value));

    /*
     * Al reformatear, React cambia el texto entero del campo y el navegador manda el cursor al
     * final. Sin esto, corregir un dígito en mitad de «130.000» te tiraba al final del número
     * en cada pulsación. `useLayoutEffect` y no `useEffect`: antes de pintar, para que no se vea
     * el salto.
     */
    useLayoutEffect(() => {
      const input = innerRef.current;
      const caret = caretRef.current;
      caretRef.current = null;
      if (input && caret !== null && document.activeElement === input) {
        input.setSelectionRange(caret, caret);
      }
    });

    return (
      <Input
        ref={(element) => {
          innerRef.current = element;
          if (typeof ref === 'function') ref(element);
          else if (ref) ref.current = element;
        }}
        inputMode="decimal"
        suffix="€"
        numeric
        value={display}
        onChange={(event) => {
          const native = event.nativeEvent as InputEvent;
          let text = event.target.value;
          let caret = event.target.selectionStart ?? text.length;

          if (native.inputType === 'insertText' && (native.data === '.' || native.data === ',')) {
            /*
             * El punto TECLEADO es la coma decimal. En el teclado numérico de un Mac o un PC
             * español la tecla de los decimales escribe un punto, y quien escribe «1250.50»
             * quiere céntimos, no un millar. Si ya había decimales, la tecla se ignora: mover
             * la coma de sitio se comería los dígitos que quedan detrás.
             */
            const before = text.slice(0, caret - 1);
            const after = text.slice(caret);
            if (display.includes(',')) {
              text = before + after;
              caret -= 1;
            } else {
              text = `${before},${after}`;
            }
          } else if (
            (native.inputType === 'deleteContentBackward' || native.inputType === 'deleteContentForward') &&
            display.length - text.length === 1 &&
            display[caret] === '.'
          ) {
            /*
             * Borrar un punto de millar no haría nada —se volvería a poner solo— y el campo
             * parecería bloqueado. Se borra el dígito de al lado, que es lo que se quería.
             */
            if (native.inputType === 'deleteContentBackward') {
              text = text.slice(0, caret - 1) + text.slice(caret);
              caret -= 1;
            } else {
              text = text.slice(0, caret) + text.slice(caret + 1);
            }
          } else if (native.inputType === 'insertFromPaste' || native.inputType === 'insertFromDrop') {
            text = normalizePastedMoney(text);
          }

          const canonical = parseMoneyDisplay(text);
          let significant = significantCount(text.slice(0, caret));
          // «,5» se completa a «0,5»: ese cero nuevo también queda a la izquierda del cursor.
          if (/^-?0\./.test(canonical) && !/^-?\d/.test(text.replace(/\./g, ''))) significant += 1;
          caretRef.current = caretForSignificant(formatMoneyDisplay(canonical), significant);

          onValueChange?.(canonical);
          onChange?.(event);
        }}
        {...rest}
      />
    );
  },
);

// ---------------------------------------------------------------------------
// Casillas y opciones
// ---------------------------------------------------------------------------

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  label: string;
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, ...rest },
  ref,
) {
  return (
    <label className={styles.checkbox}>
      <input ref={ref} type="checkbox" {...rest} />
      <span>
        {label}
        {hint && <span className={styles.checkboxHint}>{hint}</span>}
      </span>
    </label>
  );
});

interface RadioOption {
  value: string;
  title: string;
  hint?: string;
}

interface RadioGroupProps {
  name: string;
  value: string;
  options: RadioOption[];
  onChange: (value: string) => void;
  legend?: string;
}

export function RadioGroup({ name, value, options, onChange, legend }: RadioGroupProps) {
  return (
    <div className={styles.radioGroup} role="radiogroup" aria-label={legend}>
      {options.map((option) => (
        <label
          key={option.value}
          className={[styles.radioCard, value === option.value ? styles.radioCardChecked : '']
            .filter(Boolean)
            .join(' ')}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>
            <span className={styles.radioTitle}>{option.title}</span>
            {option.hint && <span className={styles.radioHint}>{option.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estructura
// ---------------------------------------------------------------------------

export function FormSection({
  title,
  hint,
  children,
}: {
  title?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.section}>
      {(title || hint) && (
        <header className={styles.sectionHeader}>
          {title && <span className={styles.sectionTitle}>{title}</span>}
          {hint && <span className={styles.sectionHint}>{hint}</span>}
        </header>
      )}
      {children}
    </section>
  );
}

/** Fila de campos que se reparte en columnas y se apila sola en pantallas estrechas. */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className={styles.row}>{children}</div>;
}

export function FormActions({ children, spread }: { children: ReactNode; spread?: boolean }) {
  return (
    <div className={[styles.actions, spread ? styles.actionsSpread : ''].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}
